// Pantalla nutricionista corporativa — reportes semanales (Sprint 6 Lote 2 T6).
// 4 tabs: Ingesta / Adherencia / Alergias / Sustituciones. Cada tab muestra
// sus propios filtros y resulta. Boton "Exportar CSV" descarga el reporte
// actualmente cargado.
import { useEffect } from 'react'
import AppHeader from '../components/AppHeader'
import { useAuth } from '../hooks/useAuth'
import { useToast } from '../components/Toast'
import {
  useReportesNutriStore,
  type TabReporteKey,
} from '../stores/reportesNutri'

const TABS: { key: TabReporteKey; label: string }[] = [
  { key: 'ingesta', label: 'Ingesta semanal' },
  { key: 'adherencia', label: 'Adherencia' },
  { key: 'alergias', label: 'Alergias' },
  { key: 'sustituciones', label: 'Sustituciones' },
]

function descargarBlob(filename: string, blob: Blob): void {
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export default function NutricionistaReportes() {
  const { user, logout } = useAuth()
  const { showToast } = useToast()
  const {
    tabActivo,
    filtros,
    loading,
    error,
    ingesta,
    adherencia,
    alergias,
    sustituciones,
    setTabActivo,
    setFiltros,
    cargarIngesta,
    cargarAdherencia,
    cargarAlergias,
    cargarSustituciones,
    exportarActual,
  } = useReportesNutriStore()

  useEffect(() => {
    if (error) showToast(error, 'error')
  }, [error, showToast])

  async function handleConsultar() {
    if (tabActivo === 'ingesta') return cargarIngesta()
    if (tabActivo === 'adherencia') return cargarAdherencia()
    if (tabActivo === 'alergias') return cargarAlergias()
    if (tabActivo === 'sustituciones') return cargarSustituciones()
  }

  async function handleExport() {
    try {
      const { filename, blob } = await exportarActual()
      descargarBlob(filename, blob)
      showToast(`Descarga: ${filename}`, 'success')
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Error al exportar', 'error')
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-gray-50">
      <AppHeader title="Nutricionista — Reportes" user={user} onLogout={logout} />

      <div
        role="tablist"
        aria-label="Pestañas de reportes"
        className="flex border-b bg-white dark:bg-gray-800 sticky top-[64px] z-10"
      >
        {TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            role="tab"
            aria-selected={tabActivo === t.key}
            aria-controls={`panel-${t.key}`}
            id={`tab-${t.key}`}
            onClick={() => setTabActivo(t.key)}
            className={`px-4 py-3 text-sm font-semibold border-b-2 ${
              tabActivo === t.key
                ? 'border-avante-primary text-avante-primary'
                : 'border-transparent text-gray-600 hover:text-gray-900'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <main className="p-4 space-y-4 max-w-6xl mx-auto">
        {/* ---------------- Filtros por tab ---------------- */}
        <section
          aria-label="Filtros"
          className="bg-white dark:bg-gray-800 rounded shadow p-3 flex flex-wrap gap-3 items-end"
        >
          <label className="block">
            <span className="text-xs font-semibold">Sede ID</span>
            <input
              type="number"
              min={1}
              value={filtros.sede_id ?? ''}
              onChange={(e) =>
                setFiltros({
                  sede_id: e.target.value ? Number(e.target.value) : null,
                })
              }
              placeholder="Todas"
              aria-label="Sede"
              className="mt-1 border rounded px-2 py-1 text-sm w-28"
            />
          </label>

          {(tabActivo === 'ingesta' || tabActivo === 'adherencia') && (
            <label className="block">
              <span className="text-xs font-semibold">Semana inicio</span>
              <input
                type="date"
                value={filtros.semana_inicio}
                onChange={(e) => setFiltros({ semana_inicio: e.target.value })}
                aria-label="Semana inicio"
                className="mt-1 border rounded px-2 py-1 text-sm"
              />
            </label>
          )}
          {tabActivo === 'adherencia' && (
            <label className="block">
              <span className="text-xs font-semibold">Paciente ID</span>
              <input
                type="number"
                min={1}
                value={filtros.paciente_id ?? ''}
                onChange={(e) =>
                  setFiltros({
                    paciente_id: e.target.value ? Number(e.target.value) : null,
                  })
                }
                aria-label="Paciente"
                className="mt-1 border rounded px-2 py-1 text-sm w-28"
              />
            </label>
          )}

          {(tabActivo === 'alergias' || tabActivo === 'sustituciones') && (
            <>
              <label className="block">
                <span className="text-xs font-semibold">Fecha inicio</span>
                <input
                  type="date"
                  value={filtros.fecha_inicio}
                  onChange={(e) => setFiltros({ fecha_inicio: e.target.value })}
                  aria-label="Fecha inicio"
                  className="mt-1 border rounded px-2 py-1 text-sm"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold">Fecha fin</span>
                <input
                  type="date"
                  value={filtros.fecha_fin}
                  onChange={(e) => setFiltros({ fecha_fin: e.target.value })}
                  aria-label="Fecha fin"
                  className="mt-1 border rounded px-2 py-1 text-sm"
                />
              </label>
            </>
          )}

          <button
            type="button"
            onClick={handleConsultar}
            disabled={loading}
            className="bg-avante-primary text-white px-3 py-2 rounded text-sm font-semibold disabled:opacity-50"
          >
            {loading ? 'Cargando…' : 'Consultar'}
          </button>
          <button
            type="button"
            onClick={handleExport}
            aria-label="Exportar CSV"
            data-testid="boton-export"
            className="border px-3 py-2 rounded text-sm font-semibold"
          >
            Exportar CSV
          </button>
        </section>

        {error && (
          <p
            role="alert"
            className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2"
          >
            {error}
          </p>
        )}

        {/* ---------------- Tab: Ingesta ---------------- */}
        {tabActivo === 'ingesta' && (
          <section
            id="panel-ingesta"
            role="tabpanel"
            aria-labelledby="tab-ingesta"
          >
            {ingesta === null && (
              <p className="text-sm text-gray-500">
                Aún no se ha consultado. Selecciona la sede y semana, luego pulsa
                «Consultar».
              </p>
            )}
            {ingesta && ingesta.length === 0 && (
              <p className="text-sm text-gray-500">
                Sin pacientes con actividad en la ventana.
              </p>
            )}
            {ingesta && ingesta.length > 0 && (
              <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded shadow">
                <table className="min-w-full text-sm">
                  <thead className="bg-gray-100 dark:bg-gray-700 text-left">
                    <tr>
                      <th className="p-2">Habitación</th>
                      <th className="p-2">Paciente</th>
                      <th className="p-2">Dieta</th>
                      <th className="p-2">Indicadas</th>
                      <th className="p-2">Servidas</th>
                      <th className="p-2">Excluidas</th>
                      <th className="p-2">Comidas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ingesta.map((r) => (
                      <tr key={`${r.paciente_id}-${r.dieta_codigo}`} className="border-t">
                        <td className="p-2 font-mono">{r.habitacion}</td>
                        <td className="p-2">{r.paciente_nombre}</td>
                        <td className="p-2 text-xs">
                          {r.dieta_codigo} — {r.dieta_nombre}
                        </td>
                        <td className="p-2 text-right">{r.lineas_indicadas}</td>
                        <td className="p-2 text-right">{r.lineas_servidas}</td>
                        <td className="p-2 text-right">{r.lineas_excluidas}</td>
                        <td className="p-2 text-right">{r.comidas_total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* ---------------- Tab: Adherencia ---------------- */}
        {tabActivo === 'adherencia' && (
          <section
            id="panel-adherencia"
            role="tabpanel"
            aria-labelledby="tab-adherencia"
          >
            {adherencia === null && (
              <p className="text-sm text-gray-500">
                Captura paciente y semana, luego «Consultar».
              </p>
            )}
            {adherencia && (
              <div className="bg-white dark:bg-gray-800 rounded shadow p-4">
                <h2 className="font-bold text-lg">
                  {adherencia.paciente_nombre ?? `Paciente #${adherencia.paciente_id}`}
                </h2>
                <p className="text-xs text-gray-500">
                  {adherencia.semana_inicio} → {adherencia.semana_fin}
                </p>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
                  <Stat
                    label="Adherencia"
                    value={`${adherencia.adherencia_pct.toFixed(1)}%`}
                    big
                  />
                  <Stat label="Lineas total" value={adherencia.lineas_total} />
                  <Stat label="Servidas" value={adherencia.lineas_servidas} />
                  <Stat label="Excluidas" value={adherencia.lineas_excluidas} />
                </div>
              </div>
            )}
          </section>
        )}

        {/* ---------------- Tab: Alergias ---------------- */}
        {tabActivo === 'alergias' && (
          <section
            id="panel-alergias"
            role="tabpanel"
            aria-labelledby="tab-alergias"
          >
            {alergias === null && (
              <p className="text-sm text-gray-500">
                Define rango de fechas y consulta.
              </p>
            )}
            {alergias && (
              <>
                <div className="flex flex-wrap gap-3 mb-3">
                  {Object.entries(alergias.totales_por_fuente).map(([k, v]) => (
                    <Chip key={k} label={`${k}: ${v}`} />
                  ))}
                  {Object.entries(alergias.severidades).map(([k, v]) => (
                    <Chip key={k} label={`${k}: ${v}`} tone="warn" />
                  ))}
                </div>
                <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded shadow">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700 text-left">
                      <tr>
                        <th className="p-2">Fecha</th>
                        <th className="p-2">Paciente</th>
                        <th className="p-2">Tipo</th>
                        <th className="p-2">Severidad</th>
                        <th className="p-2">Fuente</th>
                      </tr>
                    </thead>
                    <tbody>
                      {alergias.items.map((it) => (
                        <tr key={it.id} className="border-t">
                          <td className="p-2 text-xs">
                            {it.registrado_at.slice(0, 10)}
                          </td>
                          <td className="p-2">
                            {it.paciente_nombre}{' '}
                            <span className="text-xs text-gray-500">
                              · hab. {it.habitacion}
                            </span>
                          </td>
                          <td className="p-2 text-xs">{it.tipo_restriccion}</td>
                          <td className="p-2 text-xs">{it.severidad ?? '—'}</td>
                          <td className="p-2 text-xs">{it.fuente}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}

        {/* ---------------- Tab: Sustituciones ---------------- */}
        {tabActivo === 'sustituciones' && (
          <section
            id="panel-sustituciones"
            role="tabpanel"
            aria-labelledby="tab-sustituciones"
          >
            {sustituciones === null && (
              <p className="text-sm text-gray-500">
                Define rango de fechas y consulta.
              </p>
            )}
            {sustituciones && (
              <>
                <div className="flex flex-wrap gap-3 mb-3">
                  {Object.entries(sustituciones.totales_por_motivo).map(
                    ([k, v]) => (
                      <Chip key={k} label={`${k}: ${v}`} />
                    ),
                  )}
                </div>
                <div className="overflow-x-auto bg-white dark:bg-gray-800 rounded shadow">
                  <table className="min-w-full text-sm">
                    <thead className="bg-gray-100 dark:bg-gray-700 text-left">
                      <tr>
                        <th className="p-2">Fecha</th>
                        <th className="p-2">Origen</th>
                        <th className="p-2">Sustituto</th>
                        <th className="p-2">Motivo</th>
                        <th className="p-2">Score</th>
                        <th className="p-2">Activo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sustituciones.items.map((it) => (
                        <tr key={it.id} className="border-t">
                          <td className="p-2 text-xs">
                            {it.creado_at.slice(0, 10)}
                          </td>
                          <td className="p-2 text-xs">
                            {it.plato_origen_codigo} — {it.plato_origen_nombre}
                          </td>
                          <td className="p-2 text-xs">
                            {it.plato_sustituto_codigo} —{' '}
                            {it.plato_sustituto_nombre}
                          </td>
                          <td className="p-2 text-xs">{it.motivo_tipificado}</td>
                          <td className="p-2 text-right">
                            {Number(it.score_equivalencia).toFixed(2)}
                          </td>
                          <td className="p-2 text-xs">
                            {it.activo ? 'Si' : 'No'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </section>
        )}
      </main>
    </div>
  )
}

function Stat({
  label,
  value,
  big,
}: {
  label: string
  value: number | string
  big?: boolean
}) {
  return (
    <div className="border rounded p-2">
      <p className="text-xs text-gray-500">{label}</p>
      <p className={big ? 'text-3xl font-bold' : 'text-lg font-semibold'}>
        {value}
      </p>
    </div>
  )
}

function Chip({ label, tone }: { label: string; tone?: 'warn' }) {
  const base = 'inline-flex items-center text-xs font-semibold px-2 py-1 rounded-full'
  const palette =
    tone === 'warn'
      ? 'bg-yellow-100 text-yellow-900 border border-yellow-300'
      : 'bg-gray-100 text-gray-800 border border-gray-300'
  return <span className={`${base} ${palette}`}>{label}</span>
}
